/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { spacing, ticketColors, ticketFonts } from '../theme/public-api';
import { useModalBackdropPadding } from '../ui/hooks-ui/useModalBackdropPadding';
import { ModalScreenHeader } from '../modals/ModalScreenHeader';
import { OperationalConfirmBody } from '../modals/OperationalConfirmModal';


const NARROW_MAX_WIDTH = 420;

export type SupervisorPinModalProps = {
  visible: boolean;
  title?: string;
  message?: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (pin: string) => void | Promise<void>;
};

export function SupervisorPinModal({
  visible,
  title = 'Autorizar con PIN',
  message = 'Pedile al supervisor o dueño que ingrese el PIN de la sucursal para autorizar esta acción.',
  loading = false,
  onClose,
  onConfirm,
}: SupervisorPinModalProps) {
  const modalBackdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const useSheet = Platform.OS !== 'web' || width < NARROW_MAX_WIDTH;
  const [pin, setPin] = useState('');

  useEffect(() => {
    if (!visible) setPin('');
  }, [visible]);

  const canSubmit = pin.trim().length >= 4 && !loading;

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleConfirm = () => {
    if (!canSubmit) return;
    void onConfirm(pin.trim());
  };

  const body = (
    <>
      <OperationalConfirmBody message={message} iconName="keypad-outline" cleanDesign={useSheet}>
        <TextInput
          style={styles.pinInput}
          value={pin}
          onChangeText={(t) => setPin(t.replace(/[^\d]/g, '').slice(0, 12))}
          placeholder="PIN"
          placeholderTextColor={ticketColors.ink2}
          keyboardType="number-pad"
          secureTextEntry
          maxLength={12}
          editable={!loading}
          autoFocus={visible}
          accessibilityLabel="PIN de supervisor"
        />
      </OperationalConfirmBody>
      <View style={styles.actions}>
        <TouchableOpacity
          style={styles.cancelBtn}
          onPress={handleClose}
          disabled={loading}
          accessibilityRole="button"
        >
          <Text style={styles.cancelText}>Cancelar</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.confirmBtn, !canSubmit && styles.disabled]}
          onPress={handleConfirm}
          disabled={!canSubmit}
          accessibilityRole="button"
        >
          {loading ? (
            <ActivityIndicator color={ticketColors.paper} />
          ) : (
            <Text style={styles.confirmText}>Autorizar</Text>
          )}
        </TouchableOpacity>
      </View>
    </>
  );

  if (useSheet) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose}>
        <View style={styles.sheetBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={handleClose} />
          <View style={[styles.sheet, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
            <View style={styles.grabber} />
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>{title}</Text>
              <TouchableOpacity onPress={handleClose} style={styles.sheetCloseBtn} accessibilityRole="button">
                <Ionicons name="close" size={16} color={ticketColors.ink3} />
              </TouchableOpacity>
            </View>
            {body}
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={[styles.backdrop, modalBackdropPadding]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={handleClose} />
        <View style={styles.card}>
          <ModalScreenHeader title={title} onClose={handleClose} />
          {body}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: ticketColors.paper,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: spacing.md,
    gap: spacing.md,
  },
  sheetBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    gap: spacing.md,
  },
  grabber: {
    alignSelf: 'center',
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: ticketColors.ruleSoft,
    marginBottom: spacing.xs,
  },
  sheetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sheetTitle: {
    fontFamily: ticketFonts.sans,
    fontWeight: '800',
    fontSize: 17,
    letterSpacing: -0.3,
    color: ticketColors.ink,
  },
  sheetCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pinInput: {
    marginTop: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: 22,
    letterSpacing: 6,
    textAlign: 'center',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  cancelBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelText: {
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  confirmBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmText: {
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
    color: ticketColors.paper,
  },
  disabled: { opacity: 0.45 },
});
